import {
	Element,
	Button,
	Div,
	Span,
	reactive,
	Variable,
	VArray,
	VSet,
	onShowElement,
	ElementProperties,
	if as _if
} from 'alkali'
import * as $ from 'jquery'
import '../../styles/components/elements/dropdowns.scss' // TODO: move this
import Icon from '../Icon'
import * as RadioCheck from '../../icons/check-1.svg'
import * as MultiCheck from '../../icons/check-2.svg'
import * as MultiMixed from '../../icons/subtract-square.svg'
const MultiUnchecked = MultiMixed
const RadioUnchecked = RadioCheck
const isIE = navigator.userAgent.indexOf('Trident') > -1

// A bootstrap-less dropdown. Accepts a list of items whose options are used to create alkali elements.
export class Dropdown extends Element.with({
	visible: false,
	closeOnClick: true,
	tabIndex: -1,
	onkeydown(e) {
		const current = document.activeElement
		if (e.key === 'ArrowUp' && current.previousSibling) current.previousSibling?.focus()
		else if (e.key === 'ArrowDown' && current.nextSibling) current.nextSibling?.focus()
		else if (e.key === 'Escape') this.hide(e)
	},
}) {
	static tagName = 'dropdown'
	items: VArray<{}>
	visible: boolean
	closeOnClick: boolean
	lazy: boolean
	animationId: number
	findDropdown: any
	alignRight: boolean
	previousFocus: HTMLElement
	itemsAdded: boolean

	ready({ items, lazy }) {
		items = items instanceof Variable ? items : reactive(items)
		items = items instanceof VArray ? items : items.as(VArray)

		this.items = items
		this.lazy = !!lazy
	}

	attached() {
		if (!this.lazy && !this.itemsAdded) {
			this.itemsAdded = true
			this.append(this.items.map(item => this.getItem(item)))
		}
	}

	getItem(item) {
		if (item?.isHeader) {
			return item
		}
		return Button('.dropdown-item').with(item)
	}

	position({ offsetLeft, offsetTop, clientWidth }) {

		const height = this.clientHeight
		const availableY = window.innerHeight - offsetTop

		const top = availableY < height ? offsetTop - height + availableY - 10 : offsetTop

		this.style.top = top + 'px'

		const width = this.clientWidth
		if (this.alignRight) offsetLeft += clientWidth - width
		const availableX = window.innerWidth - offsetLeft - 10
		if (availableX < width) offsetLeft = offsetLeft - (width - availableX || 150)
		this.style.left = offsetLeft + 'px'
	}

	appendItems(items) {
		let i = 0
		const chunkSize = 10

		const process = startTime => {
			let finishTime

			do {
				const chunk = items.slice(i, i + chunkSize)
				i += chunkSize

				this.append(chunk.map(this.getItem))
				finishTime = window.performance.now()
			} while (finishTime - startTime < 10 && i < items.length)

			if (i < items.length) {
				this.animationId = requestAnimationFrame(process)
			}
		}

		this.animationId = requestAnimationFrame(process)
	}

	show(event, target?) {
		if (this.visible) return

		if (isIE) {
			$(document.body).addClass('showing-dropdown')
		}
		// TODO: This strikes me as a bit overkill
		this.style.display = ''
		target = target || event.currentTarget || event.target
		this.classList.remove('rendering-hidden')
		this.$.trigger('docdata-show')

		this.previousFocus = event.target

		if (this.lazy) {
			// Lazily append menu items on show.
			this.appendItems(this.items.valueOf())

			// Since we're manually appending instead of using Alkali's
			// DOM binding, there won't be any updates rendered if the number of
			// menu items changes while dropdown is open.

			// Updates to the _contents_ of menu items will work as normal.
		}

		// In order to remove the event listener later, we can't bind the function to this, so we
		//	 create the function here to pass it directly to hide()
		const that = this
		this.findDropdown = event => {
			let node = event.target

			while (node) {
				if (node === that && !that.closeOnClick) {
					return
				} else {
					node = node.parentElement
				}
			}

			that.hide(event)
		}

		this.style.display = 'none'
		this.visible = true
		onShowElement(this)

		if (!this.isConnected) document.body.append(this)

		const offset = $(target).offset()

		const offsetTop = offset?.top + target?.clientHeight
		let offsetLeft = offset?.left
		let clientWidth = target?.clientWidth

		_.delay(() => {
			this.classList.add('visible') // just for animation
			this.style.display = 'flex'
			document.addEventListener('click', this.findDropdown)


			// delay positioning until the dropdown is rendered for accurate placement
			this.position({ offsetTop, offsetLeft, clientWidth })
			$(this).trigger('dre-dropdown-shown')
		}, 20)
	}

	hide(event) {
		if (!this.visible) return
		if (isIE) {
			$(document.body).removeClass('showing-dropdown')
		}
		this.visible = false
		this.classList.remove('visible')
		this.classList.add('rendering-hidden')
		this.style.display = 'none'
		this.remove()
		document.removeEventListener('click', this.findDropdown)

		if (this.lazy) {
			if (this.animationId) {
				cancelAnimationFrame(this.animationId)
			}

			while (this.firstChild) {
				this.removeChild(this.firstChild)
			}
		}
		this.returnFocus(event)
	}

	returnFocus(event?) {
		if (this.previousFocus?.focus) this.previousFocus.focus()
		else if (event?.target?.focus) event.target.focus()
	}

	get $() {
		if (!this._$) {
			this._$ = $(this)
		}

		return this._$
	}
}

export type ItemKey = string | number
export type DropdownOptions = DropdownItem[] | ItemKey[] | Set<DropdownItem> | Set<ItemKey>

export interface DropdownItem {
	field: ItemKey
	label: string
}

export const getRadioItems = (
	options: DropdownOptions,
	selectedVar: Variable<ItemKey>,
	onChange?
): ElementProperties[] => {
	const items = []
	// Only default to the initial element if the selected value isn't in the items
	let first = !(
		options?.has?.(selectedVar) || options?.includes?.(selectedVar.valueOf())
	)
	for (const item of options) {
		const field = item?.field || item
		const label = item?.label || item
		const selected = selectedVar.to(select => select === field)
		items.push({
			content: [
				Icon({
					style: {
						visibility: _if(selected, ['visible', 'hidden'])
					},
					icon: _if(selected, [RadioCheck, RadioUnchecked])
				}),
				Span(label),
			],
			onclick: () => {
				selectedVar.put(field)
				onChange?.(field)
			},
			tabIndex: 0,
			attached:
				first || selectedVar.valueOf() === field
					? function() {
							_.delay(() => this.focus(), 50)
					  }
					: undefined,
		})
		first = false
	}
	return items
}

type VIterable<T> = VSet<T> | VArray<T>

export const getMultiselectItems = (
	options: DropdownOptions,
	selectedVar: VIterable<ItemKey>
): ElementProperties[] => {
	const items = []

	const size = (iterable: DropdownOptions) =>
		Array.isArray(iterable) ? iterable.length : iterable.size

	const optionsLength = size(options) || 0

	if (optionsLength > 1) {
		const selectState = selectedVar.to(selected => {
			const count = size(selected)
			return count == 0 ? 'none' : count == optionsLength ? 'all' : 'some'
		})
		const icons = {
			all: MultiCheck,
			some: MultiMixed,
			none: MultiUnchecked
		}
		items.push({
			style: {
				'font-weight': 'bold',
			},
			content: [
				Icon({
					style: {
						visibility: selectState.to(s => s == 'none' ? 'hidden' : 'visible')
					},
					icon: selectState.to(s => icons[s])
				}),
				Span({
					content: selectedVar.to(selected => {
						if (size(selected) !== optionsLength) return 'Select all'
						return 'Deselect all'
					}),
				}),
			],
			onclick: () => {
				const clear = size(selectedVar.valueOf()) === optionsLength
				if (clear) return selectedVar.clear()
				for (const option of options) {
					selectedVar.add(option?.field || option)
				}
			},
			tabIndex: 0,
		})
	}

	function onClick(event) {
		const field = event.currentTarget.dd_field

		if (selectedVar.has(field).valueOf()) selectedVar.delete(field)
		else selectedVar.add(field)
	}

	// Pending https://github.com/kriszyp/alkali/issues/47
	// We might be able to use this generator render function
	// by reference to improve performance.
	//
	// function* render() {
	//	 let selected = yield selectedVar
	//	 let field = this.dataset.field

	//	 this.classList.toggle('fa-check-square-o', selected.has('foo'))
	//	 this.classList.toggle('fa-square-o', !selected.has('foo'))
	// }

	let first = true
	for (const item of options) {
		const field = item?.field || item
		const label = item?.label || item?.field || item
		const selected = selectedVar.to(selected => selected.has?.(field) || selected.includes?.(field))
		items.push({
			content: [
				Icon({
					style: {
						visibility: _if(selected, ['visible', 'hidden'])
					},
					icon: _if(selected, [RadioCheck, RadioUnchecked])
				}),
				Span(label),
			],
			dd_field: field,
			onclick: onClick,
			tabIndex: 0,
			attached: first
				? function() {
						_.delay(() => this.focus(), 50)
				  }
				: undefined,
		})
		first = false
	}
	return items
}

export default Dropdown
